import { DRILL_DOWN_FILTER_KEYS } from '@app-types';
import type { DrillDownFilterKey, DrillDownFilters } from '@app-types';

interface DrillDownFilterLabel {
  label: string;
  /** Several values, comma-separated; the other filters take one. */
  isList: boolean;
  /** A date reads "Completed from 2026-01-01"; the other filters read "Tier ID: 2". */
  isDate: boolean;
  /** A flag reads as its label alone: "Including not completed". */
  isFlag?: boolean;
  /** How the filter is called once its ids are replaced by names, for the filters that hold ids. */
  nameLabel?: string;
}

const date = (label: string): DrillDownFilterLabel => ({ label, isList: false, isDate: true });

const DESCRIPTIONS: Record<DrillDownFilterKey, DrillDownFilterLabel> = {
  completed_from: date('Completed from'),
  completed_to: date('Completed to'),
  include_not_completed: { label: 'Including not completed', isList: false, isDate: false, isFlag: true },
  remediated_from: date('Remediated from'),
  remediated_to: date('Remediated to'),
  expired_from: date('Expired from'),
  expired_to: date('Expired to'),
  division_id: { label: 'Division ID', nameLabel: 'Division', isList: true, isDate: false },
  tier_id: { label: 'Tier ID', nameLabel: 'Tier', isList: false, isDate: false },
  gap_priority: { label: 'Gap priority', isList: false, isDate: false },
  gap_domain_id: { label: 'Gap category ID', nameLabel: 'Gap category', isList: false, isDate: false }
};

/** Names by id (as text, the way the URL holds them), for the filters that hold ids. */
export type DrillDownNames = Partial<Record<DrillDownFilterKey, ReadonlyMap<string, string>>>;

/** One applied filter in words, with the filter it stands for so it can be cleared on its own. */
export interface DrillDownFilterText {
  key: DrillDownFilterKey;
  text: string;
}

/**
 * Reads the drill-down filters out of the Assessments list URL.
 * @param params - The URL search parameters
 * @returns The filters that are present and not empty
 */
export function readDrillDownFilters(params: URLSearchParams): DrillDownFilters {
  const filters: DrillDownFilters = {};
  DRILL_DOWN_FILTER_KEYS.forEach((key) => {
    const value = params.get(key);
    if (value) {
      filters[key] = value;
    }
  });
  return filters;
}

/** The names of the ids, or undefined unless every id has one: half a filter in names would read as a different one. */
function namesOf(ids: string[], names: ReadonlyMap<string, string> | undefined): string[] | undefined {
  const resolved = ids.map((id) => names?.get(id));
  return resolved.every((name): name is string => name !== undefined) ? resolved : undefined;
}

/**
 * The filters a Dashboard Widget row put in the Assessments list URL, in words for the "Filtered from dashboard" chip.
 * @param filters - The drill-down filters read from the URL
 * @param names - Names for the ids; an id without a name (still loading, or unknown) is shown as the id
 * @returns One text per filter that is set, e.g. "Completed from 2026-01-01" or "Division: Finance"
 */
export function describeDrillDownFilters(filters: DrillDownFilters, names: DrillDownNames = {}): DrillDownFilterText[] {
  return DRILL_DOWN_FILTER_KEYS.flatMap((key) => {
    const value = filters[key];
    if (!value) {
      return [];
    }
    const { label, nameLabel, isList, isDate, isFlag } = DESCRIPTIONS[key];
    if (isFlag) {
      return [{ key, text: label }];
    }
    if (isDate) {
      return [{ key, text: `${label} ${value}` }];
    }
    const values = isList ? value.split(',') : [value];
    const resolved = nameLabel ? namesOf(values, names[key]) : undefined;
    return [{ key, text: resolved ? `${nameLabel}: ${resolved.join(', ')}` : `${label}: ${values.join(', ')}` }];
  });
}
